// Country / dial-code table behind every phone field in the app, plus the
// helpers that turn a picker choice into the ONE stored format and back.
//
// The table itself is generated (src/lib/countryDialCodes.data.ts) from
// libphonenumber-js, so it covers every calling code and the territories that
// share one; flags are derived from the ISO code, nothing is hand-typed.
//
// Stored format everywhere (profile_contacts.phone, inquiries.phone,
// listing_leads.phone): "+506 88887777", dial code, one space, national
// digits. Legacy rows saved from free-text fields may hold a bare national
// number; `normalizePhone` gives those the fallback country's dial code so
// links still work.
import { COUNTRY_DIAL_DATA, type CountryDialRow } from "./countryDialCodes.data";

export interface CountryDialCode {
  iso: string; // ISO 3166-1 alpha-2 (used as a stable key)
  name: string;
  dial: string; // e.g. "+506"
  flag: string; // emoji, derived from iso
}

interface Territory extends CountryDialCode {
  /** Full-match regex for the national numbers of a territory that shares its dial code. */
  matcher?: RegExp;
}

// Regional-indicator pair, e.g. "CR" → 🇨🇷.
export const flagFor = (iso: string): string =>
  String.fromCodePoint(...[...iso.toUpperCase()].map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65));

const toTerritory = ([iso, name, dial, leading]: CountryDialRow): Territory => ({
  iso,
  name,
  dial,
  flag: flagFor(iso),
  ...(leading ? { matcher: new RegExp(`^(?:${leading})$`) } : {}),
});

// Metadata order: grouped by dial code, the code's main country first.
const TERRITORIES: Territory[] = COUNTRY_DIAL_DATA.map(toTerritory);
const BY_ISO = new Map(TERRITORIES.map((t) => [t.iso, t]));
const BY_DIAL = new Map<string, Territory[]>();
for (const t of TERRITORIES) BY_DIAL.set(t.dial, [...(BY_DIAL.get(t.dial) ?? []), t]);
const DIALS_LONGEST_FIRST = [...BY_DIAL.keys()].sort((a, b) => b.length - a.length);

/** Every territory, alphabetical by name, for pickers. */
export const COUNTRY_DIAL_CODES: CountryDialCode[] = [...TERRITORIES]
  .map(({ iso, name, dial, flag }) => ({ iso, name, dial, flag }))
  .sort((a, b) => a.name.localeCompare(b.name, "en"));

/** Markets our users most often come from, shown first in pickers. */
export const PREFERRED_ISOS = ["CR", "US", "CA", "MX", "ES", "GB", "DE", "FR", "IT", "NL", "CH", "AR", "CO", "BR", "PA", "NI"];

export const DEFAULT_ISO = "CR";
export const DEFAULT_DIAL_CODE = "+506";

// Several countries share a dial code (e.g. US/CA = +1), so pickers key on iso
// and we derive the dial code from it.
export const findCountryByIso = (iso: string | null | undefined): CountryDialCode | undefined => {
  const t = iso ? BY_ISO.get(iso.toUpperCase()) : undefined;
  return t ? { iso: t.iso, name: t.name, dial: t.dial, flag: t.flag } : undefined;
};

// A usable fallback country: the tenant's when it is a real territory, else CR.
const safeIso = (iso: string | null | undefined): string => (iso && BY_ISO.has(iso.toUpperCase()) ? iso.toUpperCase() : DEFAULT_ISO);

// Builds the stored string, e.g. "+506 88887777". Empty when there's no number.
export const composePhone = (iso: string, number: string): string => {
  const dial = findCountryByIso(iso)?.dial ?? DEFAULT_DIAL_CODE;
  const digits = (number ?? "").replace(/\D/g, "");
  return digits ? `${dial} ${digits}` : "";
};

// Digits after an international prefix ("+" or "00"), or null when the value
// is a bare national number.
const internationalDigits = (trimmed: string): string | null => {
  if (trimmed.startsWith("+")) return trimmed.slice(1).replace(/\D/g, "");
  const digits = trimmed.replace(/\D/g, "");
  if (/^00\d/.test(trimmed.replace(/[\s()-]/g, ""))) return digits.slice(2);
  return null;
};

// Best-effort split of a stored phone ("+506 88887777") back into a country
// iso + national digits, for prefilling a picker. The LONGEST known dial code
// that prefixes the number wins (a greedy `\+\d{1,4}` regex once mis-split
// spaceless numbers like "+50688887777"); among territories sharing that code
// the one whose national-number matcher fits wins, else the code's main
// country. Bare numbers (no "+" / "00") belong to the fallback country.
/**
 * Mobile ranges a territory shares with its code's main country: Australian
 * mobiles (+61 4…) also fit Cocos and Christmas Islands, Moroccan mobiles
 * (+212 6/7…) Western Sahara. In these ranges the main country wins (payments
 * review, 2026-10-13); the territories' own numbers still go to them.
 * Mirrored in supabase/functions/_shared/monetization.ts#phoneCountry.
 */
export const MAIN_COUNTRY_SHARED_MOBILE: Readonly<Record<string, RegExp>> = { "61": /^4\d{8}$/, "212": /^[67]\d{8}$/ };

export const parsePhone = (phone: string, fallbackIso: string = DEFAULT_ISO): { iso: string; number: string } => {
  const fallback = safeIso(fallbackIso);
  const trimmed = (phone ?? "").trim();
  if (!trimmed) return { iso: fallback, number: "" };
  const intl = internationalDigits(trimmed);
  if (intl === null) return { iso: fallback, number: trimmed.replace(/\D/g, "") };
  for (const dial of DIALS_LONGEST_FIRST) {
    const code = dial.slice(1);
    if (!intl.startsWith(code)) continue;
    const national = intl.slice(code.length);
    const candidates = BY_DIAL.get(dial)!;
    const match = MAIN_COUNTRY_SHARED_MOBILE[code]?.test(national)
      ? candidates[0]
      : candidates.find((c) => c.matcher?.test(national)) ?? candidates[0];
    return { iso: match.iso, number: national };
  }
  return { iso: fallback, number: intl };
};

// Canonical stored form for ANY input: picker output, a legacy free-text
// value, "0049 170…", a bare "8888 8888" (→ fallback country). Empty when
// there are no digits.
export const normalizePhone = (raw: string | null | undefined, fallbackIso: string = DEFAULT_ISO): string => {
  const { iso, number } = parsePhone(raw ?? "", fallbackIso);
  return composePhone(iso, number);
};

// What wa.me wants: the full international number, digits only.
export const whatsappDigits = (raw: string | null | undefined, fallbackIso: string = DEFAULT_ISO): string =>
  normalizePhone(raw, fallbackIso).replace(/\D/g, "");
